// src/pages/ResourcesPublic.js
import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box, Typography, TextField, Chip, Paper, Stack, Button, Divider
} from "@mui/material";
import {
  getFirestore, collection, query, where, orderBy, getDocs
} from "firebase/firestore";


const formatDate = (v) => {
  if (!v) return "";
  const d = typeof v?.toDate === "function" ? v.toDate() : new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatAuthor = (email) => {
  if (!email) return "WorkPulse.in";
  // optional: friendly label for owner
  if (email.toLowerCase() === "khandhadiahitesh@gmail.com") return "Hitesh Khandhadia (FCA, DISA, B.Com , AICA Level-1)";
  return email;
};



export default function ResourcesPublic() {
  const db = getFirestore();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qText, setQText] = useState("");

  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const q = query(
        collection(db, "resources"),
        where("status", "==", "published"),
        orderBy("publishedAt", "desc")
      );
      const snap = await getDocs(q);
      setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    })();
  }, [db]);

  useEffect(() => {
  (async () => {
    setLoading(true);
    const q = query(
      collection(db, "resources"),
      where("status", "==", "published"),
      orderBy("publishedAt", "desc")
    );
    const snap = await getDocs(q);
    setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  })();
}, [db]);

// ✅ ADD THIS EXACTLY HERE
useEffect(() => {
  if (qText.trim()) {
    setActiveCategory("All");
  }
}, [qText]);


  const categories = useMemo(() => {
  const map = new Map();
  for (const r of rows) {
    const c = (r.category || "").trim() || "Uncategorized";
    map.set(c, (map.get(c) || 0) + 1);
  }

  // sort categories alphabetically, but keep Uncategorized at end
  const sorted = Array.from(map.entries())
    .sort((a, b) => {
      if (a[0] === "Uncategorized") return 1;
      if (b[0] === "Uncategorized") return -1;
      return a[0].localeCompare(b[0]);
    })
    .map(([name, count]) => ({ name, count }));

  const total = rows.length;
  return [{ name: "All", count: total }, ...sorted];
}, [rows]);



const filtered = useMemo(() => {
  const t = qText.trim().toLowerCase();
  const selected = (activeCategory || "All").trim();

  return rows.filter(r => {
    const cat = ((r.category || "").trim() || "Uncategorized");

    // category filter
    if (selected !== "All" && cat !== selected) return false;

    // search filter
    if (!t) return true;

    // normalize searchable fields
    const title = (r.title || "").toLowerCase();
    const excerpt = (r.excerpt || "").toLowerCase();
    const category = cat.toLowerCase();
    const tags = (r.tags || []).map(tag => tag.toLowerCase());

    // strict & predictable matching
    if (tags.includes(t)) return true;           // exact tag match
    if (title.includes(t)) return true;           // title contains
    if (excerpt.includes(t)) return true;         // excerpt contains
    if (category.includes(t)) return true;        // category contains

    return false;
  });
}, [rows, qText, activeCategory]);



  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 } }}>

      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Resources
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Practical articles and templates for CA firms using WorkPulse.in.
      </Typography>

      <Box
  sx={{
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
    gap: 2,
  }}
>
  {/* LEFT: Category partition */}
  <Paper sx={{ p: 2, height: "fit-content" }}>
    <Typography sx={{ fontWeight: 800, mb: 1 }}>Categories</Typography>
    <Stack spacing={0.5}>
      {categories.map((c) => (
        <Button
          key={c.name}
          variant={activeCategory === c.name ? "contained" : "text"}
          onClick={() => setActiveCategory(c.name)}
          sx={{
            justifyContent: "space-between",
            textTransform: "none",
            fontWeight: activeCategory === c.name ? 800 : 600,
          }}
        >
          <span>{c.name}</span>
          <span style={{ opacity: 0.75 }}>{c.count}</span>
        </Button>
      ))}
    </Stack>
  </Paper>

  {/* RIGHT: Search + List */}
  <Box>
    <Paper sx={{ p: 2, mb: 2 }}>
      <TextField
        fullWidth
        label="Search resources"
        value={qText}
        onChange={(e) => setQText(e.target.value.toLowerCase())}
      />

      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
        <Chip
          label={`Category: ${activeCategory}`}
          onDelete={activeCategory === "All" ? undefined : () => setActiveCategory("All")}
          variant="outlined"
          size="small"
        />
        {!!qText.trim() && (
          <Chip
            label={`Search: "${qText.trim()}"`}
            onDelete={() => setQText("")}
            variant="outlined"
            size="small"
          />
        )}
      </Stack>
    </Paper>

    {loading ? (
      <Typography>Loading…</Typography>
    ) : filtered.length === 0 ? (
      <Typography>No published resources found.</Typography>
    ) : (
      <Stack spacing={2}>
        {filtered.map((r) => (
          <Paper key={r.id} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {r.title}
            </Typography>


            {/* ADD THIS */}
<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
{!!r.publishedAt ? `Published: ${formatDate(r.publishedAt)} • ` : ""}
{`Author: ${formatAuthor(r.authorEmail)}`}
</Typography>


            {!!((r.category || "").trim()) && (
              <Chip
                label={(r.category || "").trim()}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            )}

            {!!r.excerpt && (
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {r.excerpt}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              {(r.tags || []).slice(0, 6).map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </Stack>

            <Box sx={{ mt: 1.5 }}>
              <Button
                component={RouterLink}
                to={`/resources/${r.slug}`}
                variant="outlined"
                size="small"
              >
                Read
              </Button>
            </Box>
          </Paper>
        ))}
      </Stack>
    )}
  </Box>
</Box>


      
    </Box>
  );
}
